import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_TRIES = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { clear } = useCart();
  const [state, setState] = useState('checking'); // checking | paid | pending | error
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setState('paid');
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (['expired', 'failed'].includes(res.data.payment_status)) { setState('error'); return; }
      } catch (e) { /* keep trying */ }
      tries += 1;
      if (tries >= MAX_TRIES) { setState('pending'); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-28 pb-20">
      <div className="max-w-lg w-full bg-cream border-t-2 border-gold p-12 text-center">
        {state === 'checking' && (<><Loader2 className="w-14 h-14 text-gold mx-auto mb-5 animate-spin" /><h1 className="font-serif text-3xl text-espresso">Confirming your payment…</h1><p className="mt-3 text-mocha">Please hold on a moment.</p></>)}
        {state === 'paid' && (<><CheckCircle2 className="w-16 h-16 text-gold mx-auto mb-5" /><h1 className="font-serif text-4xl text-espresso">Thank you for your order!</h1><p className="mt-4 text-mocha">Your payment was successful and your order is confirmed. A confirmation will follow by email.</p><Link to="/shop" className="inline-block mt-8 px-8 py-3.5 bg-espresso text-ivory text-xs tracking-[0.18em] uppercase hover:bg-gold transition-colors">Continue Shopping</Link></>)}
        {state === 'pending' && (<><Loader2 className="w-14 h-14 text-gold mx-auto mb-5" /><h1 className="font-serif text-3xl text-espresso">Payment processing</h1><p className="mt-3 text-mocha">Your payment is still being confirmed. You'll receive an email once it completes.</p><Link to="/shop" className="inline-block mt-8 px-8 py-3.5 border border-espresso text-espresso text-xs tracking-[0.18em] uppercase hover:bg-espresso hover:text-ivory transition-colors">Back to Store</Link></>)}
        {state === 'error' && (<><XCircle className="w-14 h-14 text-destructive mx-auto mb-5" /><h1 className="font-serif text-3xl text-espresso">Something went wrong</h1><p className="mt-3 text-mocha">We couldn't confirm this payment. If you were charged, please contact us.</p><Link to="/checkout" className="inline-block mt-8 px-8 py-3.5 bg-espresso text-ivory text-xs tracking-[0.18em] uppercase hover:bg-gold transition-colors">Try Again</Link></>)}
      </div>
    </div>
  );
};

export default PaymentSuccess;
